/**
 * Tedarikçi kartının "Account & history" penceresi (ADR 0119). Anahtarlar `supplierAccount.*`.
 */
export const supplierAccountEn = {
  open: 'Account & history',
  title: '{{name}} · Account & history',
  close: 'Close',
  loading: 'Loading the supplier account',
  tabs: {
    account: 'Account',
    invoices: 'Invoices',
    payments: 'Payments',
    statement: 'Statement',
    history: 'History',
  },
  balance: {
    title: 'Balance by currency',
    lead: 'Approved invoices less approved payments. Pending items are shown apart and do not change the balance.',
    empty: 'No approved invoices or payments yet.',
    invoiced: 'Invoiced',
    paid: 'Paid',
    balance: 'Balance',
    advance: 'Advance (credit)',
    pendingInvoices: 'Invoices awaiting review',
    pendingPayments: 'Payments awaiting approval',
  },
  aging: {
    title: 'Due dates',
    notDue: 'Not due',
    d1_30: '1–30 days',
    d31_60: '31–60 days',
    d61_90: '61–90 days',
    d90plus: 'Over 90 days',
    overdue: 'Overdue',
    unapplied: 'Unapplied payments',
  },
  invoices: {
    empty: 'No invoices yet.',
    lead: 'Invoices are uploaded by the supplier in the Supplier Portal, or entered by the office for a supplier that does not use the portal. They cannot be changed; a rejected invoice is replaced by a new one.',
    enter: 'Enter invoice',
    sourceOffice: 'Entered by office',
    awaitingOther: 'Awaiting another approver',
    number: 'Invoice no.',
    po: 'PO',
    date: 'Date',
    due: 'Due',
    total: 'Total',
    status: 'Status',
    match: 'Three-way match',
    actions: 'Actions',
    review: 'Review',
    file: 'Open PDF',
    overBilling: 'Over PO total: {{reason}}',
    rejectedNote: 'Rejected: {{note}}',
    matchOk: 'Matches',
  },
  enterDialog: {
    title: 'Enter supplier invoice',
    help: 'For a supplier that does not use the Supplier Portal. Enter the invoice exactly as on the supplier’s document and attach its PDF. Another person approves it; you cannot approve an invoice you entered.',
    po: 'Purchase order',
    poOption: '{{po}} · remaining {{remaining}}',
    noAward: 'without award',
    noTargets: 'No approved purchase order of this supplier can take an invoice.',
    loading: 'Loading purchase orders',
    number: 'Invoice no.',
    net: 'Net amount ({{currency}})',
    issueDate: 'Invoice date',
    dueDate: 'Due date',
    taxLabel: 'Tax',
    taxRate: 'Rate %',
    taxAmount: 'Tax amount',
    addTax: 'Add tax line',
    removeTax: 'Remove tax line',
    total: 'Total',
    overBilling: 'Reason for billing more than the order',
    overBillingHelp: 'Not yet invoiced on this order: {{remaining}}. At least 10 characters.',
    replaces: 'Replaces rejected invoice',
    replacesNone: 'None',
    file: 'Invoice PDF (8 MB at most)',
    fileTooLarge: 'The PDF is larger than 8 MB.',
    confirm: 'I checked the amounts against the supplier’s invoice',
    save: 'Save invoice',
    cancel: 'Cancel',
    errors: {
      FILE_TOO_LARGE: 'The PDF is larger than 8 MB.',
      FILE_TYPE_MISMATCH: 'The file must be a PDF.',
      INVALID_INVOICE: 'Check the invoice number, dates and amounts.',
      DUPLICATE_INVOICE_NO: 'This supplier already has an invoice with this number.',
      OVER_BILLING_REASON_REQUIRED: 'Give a reason for billing more than the order.',
      CURRENCY_MISMATCH: 'The invoice currency must be the order currency.',
      NOT_FOUND: 'The purchase order cannot take an invoice any more. Reload and try again.',
    },
  },
  bell: {
    title: 'Supplier invoices and payments',
    invoice: 'Supplier invoice to review',
    officeInvoice: 'Supplier invoice to review (entered by office)',
    payment: 'Supplier payment to approve',
    reversal: 'Payment reversal to approve',
  },
  status: {
    pending: 'Awaiting review',
    approved: 'Approved',
    rejected: 'Rejected',
  },
  paymentStatus: {
    pending: 'Awaiting approval',
    approved: 'Approved',
    rejected: 'Rejected',
    reversed: 'Reversed',
  },
  warnings: {
    OVER_PO: 'Invoiced more than the PO total',
    OVER_RECEIVED: 'Invoiced more than received on board',
    NOT_RECEIVED: 'Nothing received on board yet',
  },
  review: {
    title: 'Review invoice {{no}}',
    ownEntry: 'You entered this invoice; another person approves it. You can still reject it.',
    poTotal: 'PO total',
    received: 'Received value (GRN)',
    invoiced: 'Invoiced (incl. this invoice)',
    approved: 'Already approved',
    acknowledge: 'I have checked the warnings above and approve anyway',
    note: 'Note',
    rejectNote: 'Reason for rejection (sent to the supplier)',
    approve: 'Approve invoice',
    reject: 'Reject invoice',
    back: 'Back',
  },
  payments: {
    empty: 'No payments recorded yet.',
    lead: 'A payment counts once a second person approves it. Payments go only to a bank account approved in this company; corrections are made with a reversal.',
    record: 'Record payment',
    date: 'Value date',
    reference: 'Reference',
    amount: 'Amount',
    account: 'Bank account',
    invoice: 'Invoice',
    status: 'Status',
    recordedBy: 'Recorded by',
    approvedBy: 'Decided by',
    approve: 'Approve',
    reject: 'Reject',
    reverse: 'Reverse',
    receipt: 'Receipt',
    reversal: 'Reversal of {{reference}}',
    awaitingOther: 'Awaiting another approver',
    noBank:
      'No bank account of this supplier is approved in your company yet. Approve one under Company profile & bank first.',
  },
  payDialog: {
    title: 'Record payment',
    account: 'Bank account (approved)',
    currency: 'Currency',
    amount: 'Amount',
    valueDate: 'Value date',
    reference: 'Bank reference',
    invoice: 'Invoice (optional)',
    onAccount: 'On account (advance)',
    note: 'Note',
    receipt: 'Bank receipt (PDF or image)',
    save: 'Record payment',
    cancel: 'Cancel',
    help: 'Another person approves the payment before it counts. A newly approved bank account waits 24 hours before its first payment can be approved.',
  },
  decision: {
    approveTitle: 'Approve payment',
    approveBody:
      'The payment of {{amount}} to {{account}} becomes part of the supplier account and visible to the supplier.',
    rejectTitle: 'Reject payment',
    rejectBody: 'The record stays in the history and does not change the balance.',
    reverseTitle: 'Reverse payment',
    reverseBody: 'A reversal of the full amount is recorded; it needs a second approval too.',
    reason: 'Reason',
    approve: 'Approve',
    reject: 'Reject',
    reverse: 'Record reversal',
  },
  statement: {
    currency: 'Currency',
    from: 'From',
    to: 'To',
    show: 'Show',
    pdf: 'PDF',
    excel: 'Excel',
    empty: 'No account entries in this currency yet.',
  },
  history: {
    rate: 'Response rate',
    rateValue: '{{responded}} of {{invited}} invitations answered',
    rateNone: 'No closed invitations yet',
    openMarket: 'Quotes on open requests',
    requests: 'Requests',
    orders: 'Purchase orders',
    invoices: 'Invoices',
    payments: 'Payments',
    rfq: 'RFQ',
    vessel: 'Vessel',
    closing: 'Closing',
    invited: 'Invited',
    revisions: 'Quote versions',
    outcome: 'Result',
    yes: 'Yes',
    no: 'Open request',
    empty: 'Nothing yet.',
    cancelled: 'Cancelled',
  },
  outcome: {
    won: 'Won',
    lost: 'Not awarded',
    cancelled: 'Cancelled',
    no_response: 'No response',
    pending: 'Decision pending',
    open: 'Open',
  },
  notices: {
    approved: 'Invoice approved.',
    rejected: 'Invoice rejected.',
    paid: 'Payment recorded. Another person must approve it.',
    entered: 'Invoice entered. Another person must approve it.',
    paymentApproved: 'Payment approved.',
    paymentRejected: 'Payment rejected.',
    reversed: 'Reversal recorded. Another person must approve it.',
  },
  errors: {
    BANK_NOT_APPROVED: 'This bank account is not approved in your company.',
    BANK_CURRENCY_MISMATCH: 'The payment currency must be the bank account currency.',
    BANK_COOLING_OFF:
      'The first payment to a newly approved bank account can be approved 24 hours after the approval.',
    BANK_ACCOUNT_CHANGED:
      'The supplier changed this bank account after the payment was recorded. Record the payment again.',
    SELF_APPROVAL_FORBIDDEN: 'You recorded this payment; another person must approve it.',
    OVER_PAYMENT: 'The amount is more than the unpaid part of the invoice.',
    INVOICE_NOT_APPROVED: 'Payments can be linked only to approved invoices.',
    MATCH_WARNINGS_UNACKNOWLEDGED: 'Confirm that you checked the warnings.',
    MATCH_CHANGED: 'The order changed while you were reviewing. Review again.',
    ALREADY_REVIEWED: 'This invoice has already been decided.',
    ALREADY_DECIDED: 'This payment has already been decided.',
    ALREADY_REVERSED: 'This payment already has a reversal.',
    CONTENT_CHANGED: 'The record changed. Reload and try again.',
    INVALID_PAYMENT: 'Check the amount, date and reference.',
    INVALID_REVERSAL: 'Give a reason of at least 10 characters.',
    REASON_REQUIRED: 'A reason is required.',
    FILE_TOO_LARGE: 'The receipt is too large (25 MB at most).',
    FILE_TYPE_MISMATCH: 'The receipt must be a PDF or an image.',
    FILES_UNAVAILABLE: 'File storage is not available.',
  },
  statementDoc: {
    title: 'Statement of account',
    buyer: 'Buyer',
    supplier: 'Supplier',
    period: 'Period',
    currency: 'Currency',
    printed: 'Printed',
    opening: 'Opening balance',
    closing: 'Closing balance',
    totals: 'Totals',
    date: 'Date',
    type: 'Type',
    reference: 'Reference',
    due: 'Due',
    debit: 'Invoiced',
    credit: 'Paid',
    balance: 'Balance',
    kinds: { invoice: 'Invoice', payment: 'Payment', reversal: 'Reversal' },
    empty: 'No entries in this period.',
    footer: 'Felixmate · approved invoices and payments only',
  },
};

export const supplierAccountTr: typeof supplierAccountEn = {
  open: 'Cari ve geçmiş',
  title: '{{name}} · Cari ve geçmiş',
  close: 'Kapat',
  loading: 'Tedarikçi carisi yükleniyor',
  tabs: {
    account: 'Cari',
    invoices: 'Faturalar',
    payments: 'Ödemeler',
    statement: 'Ekstre',
    history: 'Geçmiş',
  },
  balance: {
    title: 'Para birimine göre bakiye',
    lead: 'Onaylı faturalar eksi onaylı ödemeler. Bekleyen kayıtlar ayrı görünür, bakiyeyi değiştirmez.',
    empty: 'Henüz onaylı fatura ya da ödeme yok.',
    invoiced: 'Faturalanan',
    paid: 'Ödenen',
    balance: 'Bakiye',
    advance: 'Avans (alacak)',
    pendingInvoices: 'İnceleme bekleyen faturalar',
    pendingPayments: 'Onay bekleyen ödemeler',
  },
  aging: {
    title: 'Vadeler',
    notDue: 'Vadesi gelmemiş',
    d1_30: '1–30 gün',
    d31_60: '31–60 gün',
    d61_90: '61–90 gün',
    d90plus: '90 günden fazla',
    overdue: 'Vadesi geçmiş',
    unapplied: 'Dağıtılmamış ödemeler',
  },
  invoices: {
    empty: 'Henüz fatura yok.',
    lead: 'Faturayı tedarikçi Tedarikçi Portalı’ndan yükler; portalı kullanmayan tedarikçinin faturasını ofis girer. Fatura değiştirilemez; reddedilen faturanın yerine yenisi gelir.',
    enter: 'Fatura gir',
    sourceOffice: 'Ofis girdi',
    awaitingOther: 'Başka bir onaylayıcı bekleniyor',
    number: 'Fatura no',
    po: 'Sipariş',
    date: 'Tarih',
    due: 'Vade',
    total: 'Toplam',
    status: 'Durum',
    match: 'Üç yönlü eşleştirme',
    actions: 'İşlemler',
    review: 'İncele',
    file: 'PDF’i aç',
    overBilling: 'Sipariş toplamını aşıyor: {{reason}}',
    rejectedNote: 'Reddedildi: {{note}}',
    matchOk: 'Uyumlu',
  },
  enterDialog: {
    title: 'Tedarikçi faturası gir',
    help: 'Tedarikçi Portalı’nı kullanmayan tedarikçi için. Faturayı tedarikçinin belgesindeki gibi girin ve PDF’ini ekleyin. Onayı başka bir kişi verir; girdiğiniz faturayı onaylayamazsınız.',
    po: 'Satın alma siparişi',
    poOption: '{{po}} · kalan {{remaining}}',
    noAward: 'ödülsüz',
    noTargets: 'Bu tedarikçinin fatura girilebilecek onaylı siparişi yok.',
    loading: 'Siparişler yükleniyor',
    number: 'Fatura no',
    net: 'Net tutar ({{currency}})',
    issueDate: 'Fatura tarihi',
    dueDate: 'Vade',
    taxLabel: 'Vergi',
    taxRate: 'Oran %',
    taxAmount: 'Vergi tutarı',
    addTax: 'Vergi satırı ekle',
    removeTax: 'Vergi satırını kaldır',
    total: 'Toplam',
    overBilling: 'Siparişten fazla faturalamanın gerekçesi',
    overBillingHelp: 'Bu siparişte henüz faturalanmayan: {{remaining}}. En az 10 karakter.',
    replaces: 'Yerine geçtiği reddedilen fatura',
    replacesNone: 'Yok',
    file: 'Fatura PDF’i (en çok 8 MB)',
    fileTooLarge: 'PDF 8 MB’tan büyük.',
    confirm: 'Tutarları tedarikçinin faturasıyla karşılaştırdım',
    save: 'Faturayı kaydet',
    cancel: 'Vazgeç',
    errors: {
      FILE_TOO_LARGE: 'PDF 8 MB’tan büyük.',
      FILE_TYPE_MISMATCH: 'Dosya PDF olmalı.',
      INVALID_INVOICE: 'Fatura numarasını, tarihleri ve tutarları kontrol edin.',
      DUPLICATE_INVOICE_NO: 'Bu tedarikçinin bu numarada faturası zaten var.',
      OVER_BILLING_REASON_REQUIRED: 'Siparişten fazla faturalamanın gerekçesini yazın.',
      CURRENCY_MISMATCH: 'Faturanın para birimi siparişinkiyle aynı olmalı.',
      NOT_FOUND: 'Bu siparişe artık fatura girilemiyor. Yenileyip yeniden deneyin.',
    },
  },
  bell: {
    title: 'Tedarikçi faturaları ve ödemeleri',
    invoice: 'İncelenecek tedarikçi faturası',
    officeInvoice: 'İncelenecek tedarikçi faturası (ofis girdi)',
    payment: 'Onaylanacak tedarikçi ödemesi',
    reversal: 'Onaylanacak ödeme ters kaydı',
  },
  status: {
    pending: 'İnceleme bekliyor',
    approved: 'Onaylandı',
    rejected: 'Reddedildi',
  },
  paymentStatus: {
    pending: 'Onay bekliyor',
    approved: 'Onaylandı',
    rejected: 'Reddedildi',
    reversed: 'Ters kayıtla düzeltildi',
  },
  warnings: {
    OVER_PO: 'Sipariş toplamından fazla faturalandı',
    OVER_RECEIVED: 'Gemide teslim alınandan fazla faturalandı',
    NOT_RECEIVED: 'Gemide henüz teslim alınan yok',
  },
  review: {
    title: '{{no}} numaralı faturayı incele',
    ownEntry: 'Bu faturayı siz girdiniz; onayı başka bir kişi verir. Yine de reddedebilirsiniz.',
    poTotal: 'Sipariş toplamı',
    received: 'Teslim alınan değer (GRN)',
    invoiced: 'Faturalanan (bu fatura dahil)',
    approved: 'Önceden onaylanan',
    acknowledge: 'Yukarıdaki uyarıları kontrol ettim, yine de onaylıyorum',
    note: 'Not',
    rejectNote: 'Ret gerekçesi (tedarikçiye gider)',
    approve: 'Faturayı onayla',
    reject: 'Faturayı reddet',
    back: 'Geri',
  },
  payments: {
    empty: 'Henüz ödeme kaydı yok.',
    lead: 'Ödeme, ikinci bir kişi onaylayınca sayılır. Ödeme yalnız bu şirkette onaylı banka hesabına yapılır; düzeltme ters kayıtla.',
    record: 'Ödeme kaydet',
    date: 'Valör',
    reference: 'Referans',
    amount: 'Tutar',
    account: 'Banka hesabı',
    invoice: 'Fatura',
    status: 'Durum',
    recordedBy: 'Kaydeden',
    approvedBy: 'Karar veren',
    approve: 'Onayla',
    reject: 'Reddet',
    reverse: 'Ters kayıt',
    receipt: 'Dekont',
    reversal: '{{reference}} ters kaydı',
    awaitingOther: 'Başka bir onaylayıcı bekleniyor',
    noBank:
      'Bu tedarikçinin şirketinizde onaylı banka hesabı henüz yok. Önce Şirket profili ve banka bölümünden birini onaylayın.',
  },
  payDialog: {
    title: 'Ödeme kaydet',
    account: 'Banka hesabı (onaylı)',
    currency: 'Para birimi',
    amount: 'Tutar',
    valueDate: 'Valör',
    reference: 'Banka referansı',
    invoice: 'Fatura (isteğe bağlı)',
    onAccount: 'Hesaba (avans)',
    note: 'Not',
    receipt: 'Banka dekontu (PDF ya da görüntü)',
    save: 'Ödemeyi kaydet',
    cancel: 'Vazgeç',
    help: 'Ödeme, başka bir kişi onaylayınca sayılır. Yeni onaylanan banka hesabına ilk ödemenin onayı 24 saat bekler.',
  },
  decision: {
    approveTitle: 'Ödemeyi onayla',
    approveBody:
      '{{account}} hesabına {{amount}} tutarındaki ödeme cariye girer ve tedarikçi görür.',
    rejectTitle: 'Ödemeyi reddet',
    rejectBody: 'Kayıt geçmişte kalır, bakiyeyi değiştirmez.',
    reverseTitle: 'Ödemeyi ters kayıtla düzelt',
    reverseBody: 'Tutarın tamamı için ters kayıt yazılır; o da ikinci onay ister.',
    reason: 'Gerekçe',
    approve: 'Onayla',
    reject: 'Reddet',
    reverse: 'Ters kaydı yaz',
  },
  statement: {
    currency: 'Para birimi',
    from: 'Başlangıç',
    to: 'Bitiş',
    show: 'Göster',
    pdf: 'PDF',
    excel: 'Excel',
    empty: 'Bu para biriminde henüz cari hareketi yok.',
  },
  history: {
    rate: 'Yanıt oranı',
    rateValue: '{{invited}} davetin {{responded}} tanesi yanıtlandı',
    rateNone: 'Henüz kapanmış davet yok',
    openMarket: 'Açık isteklere verilen teklifler',
    requests: 'İstekler',
    orders: 'Siparişler',
    invoices: 'Faturalar',
    payments: 'Ödemeler',
    rfq: 'RFQ',
    vessel: 'Gemi',
    closing: 'Son tarih',
    invited: 'Davetli',
    revisions: 'Teklif sürümleri',
    outcome: 'Sonuç',
    yes: 'Evet',
    no: 'Açık istek',
    empty: 'Henüz kayıt yok.',
    cancelled: 'İptal',
  },
  outcome: {
    won: 'Kazandı',
    lost: 'Verilmedi',
    cancelled: 'İptal',
    no_response: 'Yanıt yok',
    pending: 'Karar bekliyor',
    open: 'Açık',
  },
  notices: {
    approved: 'Fatura onaylandı.',
    rejected: 'Fatura reddedildi.',
    paid: 'Ödeme kaydedildi. Başka bir kişinin onaylaması gerekir.',
    entered: 'Fatura girildi. Başka bir kişinin onaylaması gerekir.',
    paymentApproved: 'Ödeme onaylandı.',
    paymentRejected: 'Ödeme reddedildi.',
    reversed: 'Ters kayıt yazıldı. Başka bir kişinin onaylaması gerekir.',
  },
  errors: {
    BANK_NOT_APPROVED: 'Bu banka hesabı şirketinizde onaylı değil.',
    BANK_CURRENCY_MISMATCH: 'Ödemenin para birimi banka hesabınınkiyle aynı olmalı.',
    BANK_COOLING_OFF:
      'Yeni onaylanan banka hesabına ilk ödeme, onaydan 24 saat sonra onaylanabilir.',
    BANK_ACCOUNT_CHANGED:
      'Tedarikçi ödeme kaydedildikten sonra bu hesabı değiştirdi. Ödemeyi yeniden kaydedin.',
    SELF_APPROVAL_FORBIDDEN: 'Bu ödemeyi siz kaydettiniz; başka bir kişi onaylamalı.',
    OVER_PAYMENT: 'Tutar faturanın ödenmemiş kısmından fazla.',
    INVOICE_NOT_APPROVED: 'Ödeme yalnız onaylı faturaya bağlanabilir.',
    MATCH_WARNINGS_UNACKNOWLEDGED: 'Uyarıları kontrol ettiğinizi işaretleyin.',
    MATCH_CHANGED: 'Siz incelerken sipariş değişti. Yeniden inceleyin.',
    ALREADY_REVIEWED: 'Bu fatura için karar verilmiş.',
    ALREADY_DECIDED: 'Bu ödeme için karar verilmiş.',
    ALREADY_REVERSED: 'Bu ödemenin ters kaydı zaten var.',
    CONTENT_CHANGED: 'Kayıt değişti. Yenileyip yeniden deneyin.',
    INVALID_PAYMENT: 'Tutarı, tarihi ve referansı kontrol edin.',
    INVALID_REVERSAL: 'En az 10 karakterlik bir gerekçe yazın.',
    REASON_REQUIRED: 'Gerekçe zorunludur.',
    FILE_TOO_LARGE: 'Dekont çok büyük (en çok 25 MB).',
    FILE_TYPE_MISMATCH: 'Dekont PDF ya da görüntü olmalı.',
    FILES_UNAVAILABLE: 'Dosya deposu kullanılamıyor.',
  },
  statementDoc: {
    title: 'Cari hesap ekstresi',
    buyer: 'Alıcı',
    supplier: 'Tedarikçi',
    period: 'Dönem',
    currency: 'Para birimi',
    printed: 'Basım',
    opening: 'Açılış bakiyesi',
    closing: 'Kapanış bakiyesi',
    totals: 'Toplamlar',
    date: 'Tarih',
    type: 'Tür',
    reference: 'Referans',
    due: 'Vade',
    debit: 'Faturalanan',
    credit: 'Ödenen',
    balance: 'Bakiye',
    kinds: { invoice: 'Fatura', payment: 'Ödeme', reversal: 'Ters kayıt' },
    empty: 'Bu dönemde hareket yok.',
    footer: 'Felixmate · yalnız onaylı faturalar ve ödemeler',
  },
};
